import { Data } from "effect"
import type React from "react"
import {
  useFetcher,
  type AppLoadContext,
  type FetcherSubmitFunction,
  type FormProps,
  type LoaderFunctionArgs,
  type RedirectFunction,
  type UNSAFE_DataWithResponseInit,
} from "react-router"

type HandlerFn<A> = (
  args: LoaderFunctionArgs<AppLoadContext>,
) => Promise<UNSAFE_DataWithResponseInit<Outcome<A>> | ReturnType<RedirectFunction>>

type InferHandlerData<T> = T extends HandlerFn<infer A> ? A : never

export const useForm = <A extends HandlerFn<any>>(): FormFetcher<InferHandlerData<A>> => {
  const fetcher = useFetcher<HandlerFn<A>>()

  const makeState = (): FormState<InferHandlerData<A>> => {
    switch (fetcher.state) {
      case "idle": {
        if (!fetcher.data) {
          return FormState.Idle()
        }

        return Outcome.isError(fetcher.data)
          ? FormState.Failed({ errors: fetcher.data.errors })
          : FormState.Succeeded({ data: fetcher.data.data as InferHandlerData<A> })
      }
      case "submitting":
      case "loading": {
        return FormState.Submitting({ formData: fetcher.formData ?? new FormData() })
      }
    }
  }

  return {
    state: makeState(),
    errors: Outcome.isError(fetcher.data) ? fetcher.data.errors : undefined,
    Form: fetcher.Form,
    submit: fetcher.submit,
  }
}

interface FormFetcher<A> {
  state: FormState<A>
  errors: ValidationErrors | undefined
  Form: React.ForwardRefExoticComponent<FormProps & React.RefAttributes<HTMLFormElement>>
  submit: FetcherSubmitFunction
}

export type FormState<A> = Data.TaggedEnum<{
  Idle: {}
  Submitting: { formData: FormData }
  Failed: { errors: ValidationErrors }
  Succeeded: { data: A }
}> & {}

export const FormState = Data.taggedEnum<FormStateDefinition>()

interface FormStateDefinition extends Data.TaggedEnum.WithGenerics<1> {
  readonly taggedEnum: FormState<this["A"]>
}

export type ValidationErrors = Record<string, string>

export type Outcome<A> = Data.TaggedEnum<{
  Success: { data: A }
  Failure: { errors: Record<string, string> }
}>

const outcome = Data.taggedEnum<OutcomeDefinition>()

export const Outcome = {
  Success: <A>(data: A) => outcome.Success({ data }),
  Failure: (errors: Record<string, string>) => outcome.Failure({ errors }),
  isError: outcome.$is("Failure"),
  isSuccess: outcome.$is("Success"),
}

interface OutcomeDefinition extends Data.TaggedEnum.WithGenerics<1> {
  readonly taggedEnum: Outcome<this["A"]>
}
